import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { getOverviewCompetitorTimeline } from '@op/services';
import { dateTypeMap, getTimeSpaceArray } from '@op/utils';
import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';

export interface IPRTimelineStatistics {
  x: never[];
  y: never[];
}

function dealWithPRTimeline(
  data: Array<{
    edition_unified_id: string;
    timeline_of_start_time: string;
    timeline_of_end_time: string;
    timeline: Array<{ time: string; [key: string]: any }>;
  }>,
  editionUnifiedId: string,
  dateType: string,
  valueKey: string,
): IPRTimelineStatistics {
  const record = data?.find(
    item => item.edition_unified_id === editionUnifiedId,
  );
  if (!record) return { x: [], y: [] };

  const { timeline, timeline_of_start_time, timeline_of_end_time } = record;
  const resolvedDateType = dateTypeMap[dateType as 'daily'] || 'day';
  const format =
    resolvedDateType === 'hour' ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD';

  const timeList: string[] = getTimeSpaceArray(
    timeline_of_start_time,
    timeline_of_end_time,
    resolvedDateType,
    format,
  );

  const valueMap: Record<string, number> = {};
  (timeline || [])
    .sort((a, b) => moment(a.time).valueOf() - moment(b.time).valueOf())
    .forEach(item => {
      valueMap[moment(item.time).format(format)] = item[valueKey] ?? 0;
    });

  const yAxisData = timeList.map(time => valueMap[time] ?? 0);
  return { x: timeList, y: yAxisData };
}

/**
 * Fetches PR timeline data once and returns processed statistics for each key.
 * Should be called in the parent after stats_list (rank) data is available.
 */
export function usePRTimeline({
  filter,
  valueMap,
  keys,
}: {
  filter: any;
  valueMap: any;
  keys: string[];
}) {
  const [statisticsMap, setStatisticsMap] = useState<
    Record<string, IPRTimelineStatistics>
  >({});

  const { loading, wrapperRequest } = useCancelRequestHook();

  const timelineFilter = useMemo(() => {
    const [start_time, end_time] = valueMap?.newTime || [];
    return {
      ...filter,
      stats_types: ['pr'],
      tab_type: 'pr',
      start_time: start_time || filter?.start_time,
      end_time: end_time || filter?.end_time,
      games: [],
    };
  }, [filter, valueMap]);

  function requestTimeline() {
    if (!timelineFilter?.edition_unified_id) return;
    wrapperRequest(cancelFunc =>
      getOverviewCompetitorTimeline(timelineFilter, cancelFunc)
        .then((data: any) => {
          const result: Record<string, IPRTimelineStatistics> = {};
          keys.forEach(key => {
            result[key] = dealWithPRTimeline(
              data,
              timelineFilter.edition_unified_id,
              timelineFilter.date_type,
              key,
            );
          });
          setStatisticsMap(result);
        })
        .catch(() => {
          setStatisticsMap({});
        }),
    );
  }

  useEffect(() => {
    setStatisticsMap({});
    requestTimeline();
  }, [timelineFilter]);

  return { statisticsMap, loading };
}
